/**
 * RIF / cédula con el prefijo (V, E, J, G, P, C) en un desplegable y el número aparte.
 * Emite SIEMPRE el formato canónico (sin importar si el usuario escribe guiones): el número se teclea solo con
 * dígitos. RIF: "X-12345678-9", con el guion antes del dígito verificador. Cédula (V o E con hasta 8 dígitos, solo
 * donde se admite: clientes): "V-12345678", sin dígito verificador.
 * Controlado (valor/onChange): sirve con react-hook-form (setValue) o con estado simple.
 */
import { useEffect, useState } from 'react';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';

export const PREFIJOS_RIF = ['V', 'E', 'J', 'G', 'P', 'C'] as const;
const ETIQUETA_PREFIJO: Record<(typeof PREFIJOS_RIF)[number], string> = { V: 'V · venezolano', E: 'E · extranjero', J: 'J · jurídico', G: 'G · gobierno', P: 'P · pasaporte', C: 'C · comuna' };

function separar(rif: string | null | undefined): { prefijo: string; digitos: string } {
  const m = /^\s*([VEJGPCvejgpc])?-?(.*)$/.exec(rif ?? '');
  const prefijo = m?.[1] ? m[1].toUpperCase() : '';
  const digitos = (m?.[2] ?? '').replace(/\D/g, '');
  return { prefijo, digitos };
}

/** "12345678-9": guion antes del dígito verificador (solo si hay al menos 2 dígitos). */
export function formatearNumeroRif(digitos: string): string {
  return digitos.length >= 2 ? `${digitos.slice(0, -1)}-${digitos.slice(-1)}` : digitos;
}

export function canonicoRif(prefijo: string, digitos: string, admiteCedula = false): string {
  if (!digitos) return '';
  // Con V o E y hasta 8 dígitos es una cédula: no tiene dígito verificador
  if (admiteCedula && (prefijo === 'V' || prefijo === 'E') && digitos.length <= 8) return `${prefijo}-${digitos}`;
  return `${prefijo}-${formatearNumeroRif(digitos)}`;
}

export function CampoRif({
  valor,
  onChange,
  error,
  id = 'c-rif',
  etiqueta = 'RIF / Cédula',
  autoFocus = false,
  className,
  admiteCedula = true,
}: {
  valor: string | null | undefined;
  onChange: (rif: string) => void;
  error?: string | undefined;
  id?: string;
  etiqueta?: string;
  autoFocus?: boolean;
  className?: string;
  /** Clientes: una persona puede registrarse con su cédula. Proveedores y empresa: solo RIF. */
  admiteCedula?: boolean;
}) {
  const inicial = separar(valor);
  const [prefijo, setPrefijo] = useState(inicial.prefijo || 'V');
  const [digitos, setDigitos] = useState(inicial.digitos);

  // Rehidrata cuando el valor externo cambia (edición de otro registro, reset del formulario)
  useEffect(() => {
    const t = setTimeout(() => {
      const s = separar(valor);
      setDigitos((actual) => (s.digitos === actual ? actual : s.digitos));
      if (s.prefijo) setPrefijo(s.prefijo);
    }, 0);
    return () => clearTimeout(t);
  }, [valor]);

  const emitir = (p: string, d: string) => onChange(canonicoRif(p, d, admiteCedula));

  return (
    <Campo etiqueta={etiqueta} htmlFor={id} error={error} ayuda={admiteCedula ? 'Solo números. Cédula: tal cual (V-12345678). RIF: con su dígito final (J-12345678-9)' : 'Solo números, sin guiones (se guarda como J-12345678-9)'} {...(className ? { className } : {})}>
      <div className="flex gap-1">
        <Select
          aria-label="Tipo de identificación"
          value={prefijo}
          onChange={(ev) => {
            setPrefijo(ev.target.value);
            emitir(ev.target.value, digitos);
          }}
          className="w-16 shrink-0 px-2"
          data-testid={`${id}-prefijo`}
          title={ETIQUETA_PREFIJO[prefijo as (typeof PREFIJOS_RIF)[number]]}
        >
          {PREFIJOS_RIF.map((p) => (
            <option key={p} value={p} title={ETIQUETA_PREFIJO[p]}>
              {p}
            </option>
          ))}
        </Select>
        <Input
          id={id}
          className="min-w-0 flex-1"
          placeholder="123456789"
          inputMode="numeric"
          maxLength={12}
          value={digitos}
          autoFocus={autoFocus}
          aria-invalid={!!error}
          data-testid={id}
          onChange={(ev) => {
            // Tolerante: si pegan "J-12345678-9" completo, el prefijo pasa al desplegable y quedan solo los dígitos
            const s = separar(ev.target.value);
            const p = s.prefijo || prefijo;
            const d = s.digitos.slice(0, 10);
            if (s.prefijo) setPrefijo(p);
            setDigitos(d);
            emitir(p, d);
          }}
        />
      </div>
    </Campo>
  );
}
